import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Tab } from '@headlessui/react';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { EditableTitle } from '../components/shared/EditableTitle';
import { ConfigForm, type ConfigFormViewState } from '../components/configs/ConfigForm';
import { ConfigModeControls } from '../components/configs/ConfigModeControls';
import { Pagination } from '../components/ui/Pagination';
import {
  useCollaborationConfig,
  useDeleteCollaborationConfig,
  useUpdateCollaborationConfig,
} from '../hooks/useCollaborationConfigs';
import DeleteResourceControl from '../components/shared/DeleteResourceControl';
import { useEndpoints } from '../hooks/useEndpoints';
import { ServiceRunsTable } from '../components/services/detail/ServiceRunsTable';
import { ResourceVersionControls } from '../components/versioning/ResourceVersionControls';
import { endpointDisplayName } from '../utils/endpointLabel';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

function EndpointsTab({ configId }: { configId: string }) {
  const [page, setPage] = useState(1);
  const limit = 10;
  const { data, isLoading } = useEndpoints(page, limit, { collaborationConfigId: configId });
  const endpoints = data?.data || [];
  const pagination = data?.pagination;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="text-gray-500 dark:text-gray-400">Loading endpoints...</div>
      </div>
    );
  }

  if (endpoints.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">No endpoints use this config.</p>
        <Link to="/endpoints" className="mt-2 inline-block text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300">
          View all endpoints
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Method</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">AI Service</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Mode</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Updated</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {endpoints.map((endpoint) => (
              <tr
                key={endpoint.id}
                className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
              >
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200">
                    {endpoint.method}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <Link
                    to={`/endpoints/${endpoint.id}`}
                    className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium"
                  >
                    {endpointDisplayName(endpoint)}
                  </Link>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {endpoint.responseQueued ? 'Async' : 'Sync'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex items-center">
                    <CalendarIcon className="h-4 w-4 mr-1" />
                    {new Date(endpoint.updatedAt).toLocaleDateString()}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pagination && pagination.totalPages > 1 && (
        <div className="mt-4">
          <Pagination
            pagination={pagination}
            onPageChange={setPage}
            onLimitChange={() => {}}
          />
        </div>
      )}
    </div>
  );
}

/**
 * The config's runs: the same runs table as the AI Service page's Runs tab
 * (task 1306), scoped to this config. A row opens its AI Service's run page.
 */
function CollaborationsTab({ configId }: { configId: string }) {
  return <ServiceRunsTable configId={configId} />;
}

/**
 * The Advanced collaboration-config page.
 *
 * Its Definition tab is a VIEW FIRST (task 806), exactly like the AI Service
 * Config tab: the config presents as a read-only rendering with Edit in the
 * panel's top-right, and Save appears beside Cancel on the first change. The
 * bottom save/cancel row is gone with it.
 */
export default function ConfigDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: config, isLoading, error } = useCollaborationConfig(id);
  const updateConfig = useUpdateCollaborationConfig();
  // Delete in the page header (task 864). A config is one of the two entities
  // whose delete can be REFUSED: if a live AI Service points at it, the dialog
  // names the services and offers no confirm at all.
  const deleteConfig = useDeleteCollaborationConfig();

  // Track name separately for the editable title
  const [configName, setConfigName] = useState('');
  const [nameChanged, setNameChanged] = useState(false);
  /** The Definition tab's mode/dirty state, rendered as this panel's controls. */
  const [formState, setFormState] = useState<ConfigFormViewState | null>(null);

  // Initialize name when config loads
  useEffect(() => {
    if (config?.name) {
      setConfigName(config.name);
    }
  }, [config?.name]);

  const handleNameChange = (newName: string) => {
    setConfigName(newName);
    setNameChanged(newName !== config?.name);
    // The title is edited outside the form, so a rename made while the panel is
    // a view would leave a dirty config with no Save on screen. Renaming IS
    // editing: it opens edit mode, where the Save it produced is visible.
    if (newName !== config?.name) formState?.edit();
  };

  const handleSubmit = async (data: {
    name: string;
    description?: string;
    definition: Record<string, unknown>;
    input?: Record<string, unknown>;
    output?: Record<string, unknown>;
    outputMapping?: Record<string, string>;
  }) => {
    if (!id) return;
    // Use the name from the editable title
    await updateConfig.mutateAsync({ id, data: { ...data, name: configName } });
    // Saving RE-BASELINES and returns to the view (task 806); it no longer
    // navigates away, because the reader is left looking at what they saved.
    setNameChanged(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Loading config...</div>
      </div>
    );
  }

  if (error || !config) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500 dark:text-red-400">
          {error ? 'Error loading config. Please try again.' : 'Config not found.'}
        </div>
      </div>
    );
  }

  const tabs = ['Definition', 'Endpoints', 'Collaborations'];

  return (
    <div>
      <div className="pb-5 border-b border-gray-200 dark:border-gray-700">
        <div className="sm:flex sm:items-start sm:justify-between">
          <div className="min-w-0">
            <EditableTitle
              value={configName}
              onChange={handleNameChange}
              placeholder="Config name"
            />
            <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
              Edit collaboration configuration
            </p>
          </div>
          <div className="mt-3 shrink-0 sm:ml-4 sm:mt-0">
            <DeleteResourceControl
              flag="innovate.fe.configs.delete"
              entity="collaboration-configs"
              entityLabel="collaboration config"
              name={configName || config.name}
              id={config.id}
              variant="button"
              body={<p>The agents it names stay, because other collaborations may use them.</p>}
              onDelete={() => deleteConfig.mutateAsync(config.id)}
              onDeleted={() => navigate('/configs')}
            />
          </div>
        </div>
        {/* Version status + Publish (task 725) — read-only indicator, separate
            from any action. Invisible vocabulary-wise until the account adopts
            Publishing & versions. */}
        <ResourceVersionControls
          entityType="collaboration_config"
          entityId={id}
          resourceName={configName}
          className="mt-3"
        />
      </div>

      <Tab.Group>
        <Tab.List className="mt-6 flex space-x-1 border-b border-gray-200 dark:border-gray-700">
          {tabs.map((tab) => (
            <Tab
              key={tab}
              className={({ selected }) =>
                classNames(
                  'px-4 py-2 text-sm font-medium focus:outline-none',
                  selected
                    ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                )
              }
            >
              {tab}
            </Tab>
          ))}
        </Tab.List>
        <Tab.Panels className="mt-6">
          <Tab.Panel>
            {/* The panel's own chrome: Edit in the view, Cancel + Save (once
                dirty) while editing — top-right, above the form. */}
            <div className="mb-4 flex justify-end">
              <ConfigModeControls state={formState} />
            </div>
            <ConfigForm
              initialData={config}
              onSubmit={handleSubmit}
              mode="edit"
              nameChanged={nameChanged}
              viewModeEnabled
              onViewStateChange={setFormState}
            />
          </Tab.Panel>
          <Tab.Panel>
            {id && <EndpointsTab configId={id} />}
          </Tab.Panel>
          <Tab.Panel>
            {id && <CollaborationsTab configId={id} />}
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}
